import type { RingClientApi } from "../../shared/api-contract";
import type { DomainAdapter, DomainId, WriteOp } from "./types";

type Listener = () => void;

interface Slice {
  state: unknown;
  /** Bumped on every mutate/setState; guards last-write-wins reconciles. */
  seq: number;
  seeded: boolean;
  listeners: Set<Listener>;
  pollRefs: number;
  pollTimer: number | null;
  pollMs: number;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Recursive merge: plain objects merge key-by-key; arrays/primitives replace. */
export function deepMerge<T>(base: T, patch: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(patch)) return patch as T;
  const out: Record<string, unknown> = { ...base };
  for (const k of Object.keys(patch)) {
    out[k] = k in base ? deepMerge((base as Record<string, unknown>)[k], patch[k]) : patch[k];
  }
  return out as T;
}

/**
 * Single source of editor state across every tab/panel. Domains are isolated
 * slices subscribed via useSyncExternalStore; `mutate` centralizes the
 * optimistic-patch + seq-guarded last-response-wins reconcile pattern that the
 * old LightsMenu implemented by hand. Nothing here is React-aware.
 */
export class EditorStore {
  private slices = new Map<DomainId, Slice>();
  /** Monotonic counter bumped on every notify — lets a multi-domain hook use
   * a single primitive snapshot (it only re-reads when a subscribed domain
   * actually fired its listener). */
  private tick = 0;
  onError: ((e: Error) => void) | null = null;

  constructor(
    private ring: RingClientApi,
    private adapters: Partial<Record<DomainId, DomainAdapter>>,
  ) {
    for (const [id, a] of Object.entries(adapters) as [DomainId, DomainAdapter][]) {
      this.slices.set(id, {
        state: a.initial,
        seq: 0,
        seeded: a.initial !== undefined && !a.seed,
        listeners: new Set(),
        pollRefs: 0,
        pollTimer: null,
        pollMs: 0,
      });
    }
  }

  private slice(domain: DomainId): Slice {
    let s = this.slices.get(domain);
    if (!s) {
      s = { state: undefined, seq: 0, seeded: false, listeners: new Set(), pollRefs: 0, pollTimer: null, pollMs: 0 };
      this.slices.set(domain, s);
    }
    return s;
  }

  getState(domain: DomainId): unknown {
    return this.slice(domain).state;
  }

  subscribe(domain: DomainId, fn: Listener): () => void {
    const s = this.slice(domain);
    s.listeners.add(fn);
    // Lazy-seed a domain the first time anything subscribes to it.
    if (!s.seeded) void this.refresh(domain);
    return () => s.listeners.delete(fn);
  }

  private notify(domain: DomainId) {
    this.tick++;
    for (const fn of this.slice(domain).listeners) fn();
  }

  /** Global mutation counter — snapshot for multi-domain subscriptions. */
  getTick(): number {
    return this.tick;
  }

  /** Replace a slice's state outright (event-driven updates, selection change). */
  setState(domain: DomainId, next: unknown) {
    const s = this.slice(domain);
    s.seq++; // cancel any in-flight reconcile — the external update wins
    s.state = next;
    s.seeded = true;
    this.notify(domain);
  }

  /**
   * Apply one write. The returned promise settles once the RPC has resolved
   * (and reconciled) or failed; it never rejects. Inspector rows ignore it —
   * they are fire-and-forget by design — but a caller that writes several
   * fields as ONE step (Basic mode, AI Look Match) awaits it so the next step
   * reads what the viewer actually holds.
   */
  mutate(domain: DomainId, op: WriteOp): Promise<void> {
    const s = this.slice(domain);
    const seq = ++s.seq;
    if (op.patch !== undefined) {
      s.state = deepMerge(s.state, op.patch);
      this.notify(domain);
    }
    if (!op.call) return Promise.resolve();
    return op.call(this.ring).then(
      (res) => {
        if (seq !== s.seq) return; // a newer write superseded us
        if (op.reconcile) {
          s.state = op.reconcile(res, s.state);
          this.notify(domain);
        }
      },
      (err: Error) => {
        this.onError?.(err);
        void this.refresh(domain); // pull the truth back after a failed write
      },
    );
  }

  /** Re-run a domain's seed reader (and notify). No-op for seedless domains. */
  async refresh(domain: DomainId): Promise<void> {
    const a = this.adapters[domain];
    const s = this.slice(domain);
    if (!a?.seed) {
      s.seeded = true;
      return;
    }
    const expect = ++s.seq;
    try {
      const next = await a.seed(this.ring, (d) => this.getState(d));
      if (expect !== s.seq) return; // superseded
      s.seeded = true;
      // A getter that resolves undefined must not clobber the slice's initial
      // (would break consumers that read .length / fields straight away).
      if (next !== undefined) {
        s.state = next;
        this.notify(domain);
      }
    } catch (err) {
      this.onError?.(err as Error);
    }
  }

  /** Eagerly seed the domains marked `eager` (called once at provider mount). */
  async seedEager(): Promise<void> {
    const ids = (Object.entries(this.adapters) as [DomainId, DomainAdapter][])
      .filter(([, a]) => a.eager && a.seed)
      .map(([id]) => id);
    await Promise.all(ids.map((id) => this.refresh(id)));
  }

  /** Register interest in periodic refresh (monitor rows). Returns cleanup. */
  addPollRef(domain: DomainId, ms: number): () => void {
    const s = this.slice(domain);
    s.pollRefs++;
    s.pollMs = s.pollMs ? Math.min(s.pollMs, ms) : ms;
    if (s.pollTimer == null) {
      s.pollTimer = window.setInterval(() => void this.refresh(domain), s.pollMs);
    }
    return () => {
      s.pollRefs--;
      if (s.pollRefs <= 0 && s.pollTimer != null) {
        window.clearInterval(s.pollTimer);
        s.pollTimer = null;
        s.pollMs = 0;
      }
    };
  }

  dispose() {
    for (const s of this.slices.values()) {
      if (s.pollTimer != null) window.clearInterval(s.pollTimer);
      s.listeners.clear();
    }
    this.slices.clear();
  }
}
